"use client";

import React from "react";
import { GraduationCap, Wrench, Database, Headphones, ArrowRight, ShieldCheck, Check } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
const services = [{
  title: "Technical Training & Field Calibration",
  category: "Training & Calibration",
  description: "Structured operator training programs and on-site instrument calibration traceable to national and international measurement standards.",
  icon: GraduationCap,
  highlights: ["On-site & remote operator sessions", "Traceable calibration standards", "Compliance audit documentation"],
  link: "/TrainingApplicationSupportPage",
  ctaLabel: "View Training Details"
}, {
  title: "Preventative Maintenance & Service Contracts",
  category: "Maintenance Plans",
  description: "Scheduled preventive maintenance routines, optic cleaning, component diagnostics, and structured annual SLA service contracts.",
  icon: Wrench,
  highlights: ["Scheduled preventative visits", "Certified OEM spare parts", "Priority breakdown dispatch"],
  link: "/PreventiveMaintenanceAndRepairsPage",
  ctaLabel: "View Service Plans"
}, {
  title: "Spectral Library Updates & Integration",
  category: "Software & Data",
  description: "Custom chemometric model maintenance, Raman and NIR database library expansion, and direct integration into LIMS and PAT software.",
  icon: Database,
  highlights: ["Reference spectral updates", "Chemometric model optimization", "LIMS & SCADA interfacing"],
  link: "/QualificationLifecycleServicesPage",
  ctaLabel: "Explore Integration"
}, {
  title: "Rapid Technical Support",
  category: "Helpdesk & Dispatch",
  description: "Direct technical consultation with analytical engineers for fault isolation, remote system diagnostics, and expedited on-site repair visits.",
  icon: Headphones,
  highlights: ["Direct technical specialist contact", "Secure remote telemetry diagnostics", "Guaranteed response windows"],
  link: "/SupportRequestPage",
  ctaLabel: "Request Support"
}];
export default function TechnicalServices() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="technicalservicesgrid" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border/60">
      <div className="page-container mx-auto">
        {/* Section Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12 md:mb-16">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 mb-3">
              <Badge variant="secondary" className="px-2.5 py-1 text-xs font-medium">
                <ShieldCheck className="size-3.5 text-primary mr-1" />
                Lifecycle Technical Support
              </Badge>
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground">
              Technical Services & System Maintenance
            </h2>
            <p className="mt-3 text-base text-muted-foreground leading-relaxed">
              Factory-certified engineering support, instrument qualification, and analytical lifecycle care across Ireland and the UK.
            </p>
          </div>
          <div className="shrink-0">
            <Button variant="outline" asChild className="h-10 px-4">
              <Link to="/ContactPage">
                Contact Service Desk
                <ArrowRight className="size-4 ml-1.5" />
              </Link>
            </Button>
          </div>
        </div>

        {/* 4-Column Service Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service, index) => {
          const IconComponent = service.icon;
          return <motion.div key={service.title} data-index={index} initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: "-40px"
          }} transition={{
            duration: 0.4,
            delay: shouldReduceMotion ? 0 : index * 0.1
          }} className="flex">
                <Card className="flex flex-col justify-between w-full border border-border bg-card shadow-xs hover:shadow-md transition-[box-shadow,border-color] hover:border-primary/40 rounded-xl overflow-hidden group">
                  <div>
                    <CardHeader className="pb-4">
                      <div className="flex items-center justify-between gap-2 mb-3">
                        <div className="size-11 rounded-lg bg-secondary flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                          <IconComponent className="size-5" />
                        </div>
                        <span className="text-xs font-medium text-muted-foreground bg-muted/60 px-2 py-0.5 rounded">
                          {service.category}
                        </span>
                      </div>
                      <CardTitle className="text-lg font-semibold text-card-foreground leading-snug">
                        {service.title}
                      </CardTitle>
                    </CardHeader>

                    <CardContent className="space-y-4">
                      <CardDescription className="text-sm text-muted-foreground leading-relaxed">
                        {service.description}
                      </CardDescription>

                      <div className="pt-2 border-t border-border/50">
                        <ul className="space-y-2">
                          {service.highlights.map((highlight, hIndex) => <li key={hIndex} className="flex items-start text-xs text-foreground/80 gap-2">
                              <Check className="size-3.5 text-primary shrink-0 mt-0.5" />
                              <span>{highlight}</span>
                            </li>)}
                        </ul>
                      </div>
                    </CardContent>
                  </div>

                  <CardFooter className="pt-4 border-t border-border/40 mt-auto">
                    <Button variant="ghost" size="sm" asChild className="w-full justify-between px-2 text-primary hover:text-primary hover:bg-primary/10 group/btn">
                      <Link to={service.link}>
                        <span className="font-medium text-xs">{service.ctaLabel}</span>
                        <ArrowRight className="size-3.5 transition-transform group-hover/btn:translate-x-1" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>;
        })}
        </div>
      </div>
    </section>;
}